import React, { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { T } from "react-translator-component";
import { getCDNImage } from "../../globals";
import { currencyFormat, getAvailableHours, i18nValue, priceWithoutTax } from "../../functions";
import { HotelContext } from "../../context/hotel";
import processHtml from "../processHtml";
import { RoomService } from "../Icons";
import { hotelShowsPriceWithoutTaxes } from "../../components/CheckIn/hotelConditions";

const quantityButtonStyle = {
  width: 36,
  height: 36,
  borderRadius: 12,
  border: "none",
  background: "#82378A",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 10px 24px rgba(130,55,138,0.2)",
};

const FONT_SIZE_MAP = {
  horizontal: {
    small: {
      title: "0.98rem",
      description: "0.74rem",
      price: "1.2rem",
    },
    medium: {
      title: "1.12rem",
      description: "0.82rem",
      price: "1.45rem",
    },
    large: {
      title: "1.24rem",
      description: "0.9rem",
      price: "1.7rem",
    },
  },
  vertical: {
    small: {
      title: "1.2rem",
      description: "0.84rem",
      price: "1.65rem",
    },
    medium: {
      title: "1.55rem",
      description: "0.95rem",
      price: "2.1rem",
    },
    large: {
      title: "1.9rem",
      description: "1.05rem",
      price: "2.45rem",
    },
  },
};

const MenuCommerceItemCard = ({ item, quantity = 0, language, currency, appearance, onAdd, onRemove, showBuyButton = true }) => {
  const [disabled, setDisabled] = useState(false);
  const { data: hotel } = useContext(HotelContext);
  const hotelId = hotel?.id;
  const hasTaxes = hotelShowsPriceWithoutTaxes(hotelId);

  useEffect(() => {
    if (!item?.disableTimeRanges) {
      setDisabled(false);
      return;
    }

    const disabledByHour = getAvailableHours(item.disableTimeRanges);
    setDisabled(Boolean(disabledByHour?.isDisabled));
  }, [item]);

  const itemName = i18nValue(item, "name", language, null, item?.name);
  const itemDescription = i18nValue(item, "description", language, null, item?.description);
  const hasPreferences = Object.keys(item?.preferences || {}).length > 0;
  const layout = appearance?.cardLayout === "vertical" ? "vertical" : "horizontal";
  const titleSize = FONT_SIZE_MAP[layout][appearance?.titleFontSize || "medium"] || FONT_SIZE_MAP[layout].medium;
  const descriptionSize =
    FONT_SIZE_MAP[layout][appearance?.descriptionFontSize || "medium"] || FONT_SIZE_MAP[layout].medium;
  const priceSize = FONT_SIZE_MAP[layout][appearance?.priceFontSize || "medium"] || FONT_SIZE_MAP[layout].medium;
  const placeOrderLabel = T(layout === "horizontal" ? "general.buy" : "general.placeOrder");
  const addButtonStyle =
    layout === "horizontal"
      ? {
          minHeight: 52,
          borderRadius: 16,
          border: "none",
          background: "#82378A",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 10px 24px rgba(130,55,138,0.2)",
        }
      : {
          ...quantityButtonStyle,
          borderRadius: 16,
        };

  return (
    <article
      className={`menu-commerce-item-card menu-commerce-item-card--${layout}`}
      style={{
        background: disabled ? "#F8F9FC" : "#FFFFFF",
        border: disabled ? "1px solid rgba(244,67,54,0.14)" : "1px solid rgba(130,55,138,0.12)",
        boxShadow: disabled ? "none" : "0 14px 28px rgba(15,23,42,0.06)",
        opacity: disabled ? 0.72 : 1,
      }}
    >
      <div className={`menu-commerce-item-layout menu-commerce-item-layout--${layout}`}>
        <div className="menu-commerce-item-image">
          {item?.photo ? (
            <img src={getCDNImage(item.photo)} alt={itemName} />
          ) : (
            <div className="w-100 h-100 d-flex align-items-center justify-content-center">
              <RoomService width={38} height={38} />
            </div>
          )}
        </div>

        <div className={`menu-commerce-item-copy menu-commerce-item-copy--${layout}`}>
          <div className="menu-commerce-item-type">
            {item?.type || "menu"}
          </div>

          <h5 className="menu-commerce-item-title" style={{ fontSize: titleSize.title }}>{itemName}</h5>

          {itemDescription ? (
            <div className="menu-commerce-item-description" style={{ fontSize: descriptionSize.description }}>
              {processHtml(itemDescription)}
            </div>
          ) : null}

          <div className={`menu-commerce-item-footer menu-commerce-item-footer--${layout}`}>
            <div className="menu-commerce-price-block">
              <div className="menu-commerce-price" style={{ fontSize: priceSize.price }}>
                {item?.price > 0 ? currencyFormat(item.price, currency) : T("general.no-charge")}
              </div>
              {hasTaxes && item?.price > 0 ? (
                <div className="menu-commerce-price-note">
                  {T("menu.price_without_taxes")} {currencyFormat(priceWithoutTax(item.price), currency)}
                </div>
              ) : null}
            </div>

            {!disabled && showBuyButton ? (
              <div className={`menu-commerce-item-action menu-commerce-item-action--${layout}`}>
                {quantity > 0 ? (
                  <div className={`menu-commerce-quantity-control menu-commerce-quantity-control--${layout}`}>
                    <Button style={quantityButtonStyle} onClick={() => onRemove(item.id)}>
                      <FontAwesomeIcon icon={faMinus} />
                    </Button>
                    <span className="menu-commerce-quantity-value">{quantity}</span>
                    <Button style={quantityButtonStyle} onClick={() => onAdd(item.id)}>
                      <FontAwesomeIcon icon={faPlus} />
                    </Button>
                  </div>
                ) : (
                  <Button
                    className={`menu-commerce-add-button menu-commerce-add-button--${layout}`}
                    style={addButtonStyle}
                    onClick={() => onAdd(item.id)}
                  >
                    {layout === "horizontal" ? placeOrderLabel : <FontAwesomeIcon icon={faPlus} />}
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showBuyButton && hasPreferences && quantity > 0 ? (
        <div
          className="menu-commerce-item-message"
          style={{
            background: "rgba(255,184,34,0.12)",
            color: "#B7791F",
          }}
        >
          {T("request.customize_next")}
        </div>
      ) : null}

      {disabled ? (
        <div
          className="menu-commerce-item-message"
          style={{
            background: "rgba(244,67,54,0.08)",
            color: "#D9214E",
          }}
        >
          {T("request.item-out-of-hours")}
        </div>
      ) : null}
    </article>
  );
};

export default MenuCommerceItemCard;
